// utils.js - файл с утилитарными функциями для работы со статистикой (ОБНОВЛЕННЫЙ)

import { format, parseISO, differenceInDays } from 'date-fns';
import React, { useState } from 'react';
import { 
  isColumnType, 
  getColumnByKey,
  fetchColumnsMetadata 
} from './columnsAPI';

// ========================================
// ФУНКЦИИ ДЛЯ РАБОТЫ С КОЛОНКАМИ (ОБНОВЛЕННЫЕ)
// ========================================

/**
 * Проверяет, является ли поле категорией ошибок
 * @param {string} field - Ключ поля
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {boolean}
 */
export const isErrorCategory = (field, metadata = []) => {
  return isColumnType(field, 'error_category', metadata);
};

/**
 * Проверяет, является ли поле процентной колонкой
 * @param {string} field - Ключ поля
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {boolean}
 */
export const isErrorPercentage = (field, metadata = []) => {
  const column = getColumnByKey(field, metadata);
  return column?.is_percentage === true;
};

/**
 * Получает базовую категорию из процентной колонки
 * @param {string} percentageField - Ключ процентной колонки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {string|null}
 */
export const getBaseCategoryFromPercentage = (percentageField, metadata = []) => {
  const column = getColumnByKey(percentageField, metadata);
  if (!column?.is_percentage) return null;
  
  const config = column.calculation_config || {};
  
  // Возвращаем numerator для percentage_ratio или target для percentage_attempts
  return config.numerator || config.target || null;
};

/**
 * Создает имя процентной колонки из базовой категории
 * @param {string} category - Ключ базовой категории
 * @returns {string}
 */
export const getPercentageColumnName = (category) => {
  return `${category}_percentage`;
};

/**
 * Обрабатывает категории ошибок в данных (ОБНОВЛЕННАЯ ВЕРСИЯ)
 * @param {Array} data - Массив данных статистики
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {Array} Массив ключей категорий ошибок
 */
export const processErrorCategories = (data, metadata = []) => {
  if (!data || !Array.isArray(data) || data.length === 0) return [];
  
  // Получаем категории ошибок из метаданных
  const errorCategories = metadata
    .filter(col => col.column_type === 'error_category')
    .map(col => col.key);
  
  // Проверяем, какие из них действительно присутствуют в данных
  const firstItem = data[0];
  return errorCategories.filter(category => 
    firstItem.hasOwnProperty(category)
  );
};

/**
 * Определяет базу для расчета процентов категории ошибок
 * @param {string} category - Категория ошибки
 * @param {Array} metadata - Массив метаданных колонок
 * @returns {string}
 */
export const getErrorBaseForPercentage = (category, metadata = []) => {
  const column = getColumnByKey(category, metadata);
  if (!column) {
    // Fallback к старой логике для совместимости
    if (category.toLowerCase().includes('otp')) {
      return 'sms_success';
    } else if (category.toLowerCase().includes('validation')) {
      return 'registration_success';
    }
    return 'registration_success';
  }
  
  // Используем конфигурацию из метаданных
  const config = column.calculation_config || {};
  return config.denominator || 'registration_success';
};

// ========================================
// ФУНКЦИИ ДЛЯ РАБОТЫ С URL (БЕЗ ИЗМЕНЕНИЙ)
// ========================================

/**
 * Получает параметры из URL
 * @returns {Object} Объект с параметрами
 */
export const getQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  
  // Для каждого параметра сначала проверяем его наличие
  const columnsParam = params.get('columns');
  const orderParam = params.get('order');
  const landingsParam = params.get('landings');
  const countryParam = params.get('country');
  const dateRangeParam = params.get('dateRange');
  const trafficSourceParam = params.get('trafficSource');

  let dateRange = null;
  if (dateRangeParam) {
    try {
      const parsedRange = JSON.parse(decodeURIComponent(dateRangeParam));
      dateRange = parsedRange.map(range => ({
        ...range,
        startDate: range.startDate ? parseISO(range.startDate) : null,
        endDate: range.endDate ? parseISO(range.endDate) : null,
        key: range.key
      }));
    } catch (e) {
      console.error('Error parsing dateRange:', e);
    }
  }
  
  let trafficSource = null;
  if (trafficSourceParam) {
    try {
      trafficSource = JSON.parse(decodeURIComponent(trafficSourceParam));
    } catch (e) {
      console.error('Error parsing trafficSource:', e);
    }
  }
  
  return {
    visibleColumns: columnsParam ? JSON.parse(decodeURIComponent(columnsParam)) : null,
    columnOrder: orderParam ? JSON.parse(decodeURIComponent(orderParam)) : null,
    domain: params.get('domain') || null,
    landings: landingsParam ? JSON.parse(decodeURIComponent(landingsParam)) : null,
    country: countryParam ? JSON.parse(decodeURIComponent(countryParam)) : null,
    trafficSource: trafficSource,
    dateRange
  };
};

/**
 * Обновляет параметры URL
 * @param {Object} params - Объект с параметрами
 * @returns {string} Сформированная ссылка
 */
export const updateQueryParams = (params) => {
  const urlParams = new URLSearchParams();
  
  if (params.domain && params.domain !== 'undefined') {
    urlParams.set('domain', params.domain);
  }
  if (params.landings && Array.isArray(params.landings)) {
    urlParams.set('landings', encodeURIComponent(JSON.stringify(params.landings)));
  }
  if (params.country && params.country.value) {
    urlParams.set('country', encodeURIComponent(JSON.stringify(params.country)));
  }
  if (params.trafficSource && params.trafficSource.value) {
    urlParams.set('trafficSource', encodeURIComponent(JSON.stringify(params.trafficSource)));
  }
  if (params.dateRange) {
    const dateRangeForUrl = params.dateRange.map(range => ({
      ...range,
      startDate: range.startDate instanceof Date ? range.startDate.toISOString() : range.startDate,
      endDate: range.endDate instanceof Date ? range.endDate.toISOString() : range.endDate,
      key: range.key
    }));
    urlParams.set('dateRange', encodeURIComponent(JSON.stringify(dateRangeForUrl)));
  }
  if (params.visibleColumns && Array.isArray(params.visibleColumns)) {
    urlParams.set('columns', encodeURIComponent(JSON.stringify(params.visibleColumns)));
  }
  if (params.columnOrder && Array.isArray(params.columnOrder)) {
    urlParams.set('order', encodeURIComponent(JSON.stringify(params.columnOrder)));
  }
 
  // Вместо изменения URL возвращаем сформированную ссылку
  return `${window.location.origin}${window.location.pathname}?${urlParams.toString()}`;
};

// ========================================
// ФУНКЦИИ ДЛЯ РАБОТЫ С ДАТАМИ (БЕЗ ИЗМЕНЕНИЙ)
// ========================================

/**
 * Проверяет и преобразует дату (для безопасной работы с date-fns)
 * @param {*} date - Значение даты
 * @returns {Date|null}
 */
export const ensureDate = (date) => {
  if (!date) return null;
  
  if (date instanceof Date) {
    return date;
  }
  
  if (typeof date === 'string') {
    try {
      return parseISO(date);
    } catch (e) {
      console.error('Error parsing date string:', date, e);
      return null;
    }
  }
  
  return null;
};

/**
 * Определяет период на основе выбранного диапазона дат
 * @param {Array} dateRange - Массив с диапазоном дат
 * @returns {string}
 */
export const determinePeriodFromDateRange = (dateRange) => {
  if (!dateRange || !dateRange[0] || !dateRange[0].startDate || !dateRange[0].endDate) {
    return 'day'; // Значение по умолчанию
  }
  
  // Убедимся, что даты - это объекты Date
  const startDate = ensureDate(dateRange[0].startDate);
  const endDate = ensureDate(dateRange[0].endDate);
  
  if (!startDate || !endDate) {
    return 'day';
  }
  
  try {
    const days = differenceInDays(endDate, startDate);
    
    if (days <= 3) {
      return 'hour';
    } else if (days <= 7) {
      return 'hour4';
    } else {
      return 'day';
    }
  } catch (e) {
    console.error('Error calculating date difference:', e);
    return 'day';
  }
};

/**
 * Создает сегодняшний диапазон дат
 * @returns {Object}
 */
export const getTodayDateRange = () => {
  const today = new Date();
  return {
    startDate: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0),
    endDate: new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999),
    key: 'selection'
  };
};

// ========================================
// ХУКИ И ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ (БЕЗ ИЗМЕНЕНИЙ)
// ========================================

/**
 * Пользовательский хук для работы с localStorage
 * @param {string} key - Ключ в localStorage
 * @param {*} initialValue - Начальное значение
 * @returns {Array} [storedValue, setValue]
 */
export const useLocalStorage = (key, initialValue) => {
  // Состояние для хранения значения
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Получаем значение из localStorage
      const item = localStorage.getItem(key);
      // Возвращаем сохраненное значение или initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Функция для обновления значения
  const setValue = (value) => {
    try {
      // Сохраняем состояние
      setStoredValue(value);
      // Сохраняем в localStorage
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
};

// ========================================
// ФУНКЦИИ ДЛЯ ФОРМАТИРОВАНИЯ (НОВЫЕ)
// ========================================

/**
 * Форматирует число с разделителями тысяч
 * @param {number} number - Число для форматирования
 * @returns {string}
 */
export const formatNumber = (number) => {
  if (typeof number !== 'number') return number;
  return number.toLocaleString('en-US');
};

/**
 * Форматирует значение колонки в зависимости от ее типа
 * @param {*} value - Значение для форматирования
 * @param {Object} column - Объект колонки из метаданных
 * @returns {string}
 */
export const formatColumnValue = (value, column) => {
  if (value === null || value === undefined) return '';
  
  if (column?.is_percentage) {
    return `${value}%`;
  }
  
  if (typeof value === 'number') {
    return formatNumber(value);
  }
  
  return value.toString();
};

// ========================================
// ФУНКЦИИ ДЛЯ СОВМЕСТИМОСТИ (DEPRECATED)
// ========================================

/**
 * @deprecated Используйте processErrorCategories с metadata параметром
 * Обрабатывает категории ошибок в данных (старая версия для совместимости)
 */
export const processErrorCategoriesLegacy = (data) => {
  console.warn('processErrorCategoriesLegacy is deprecated. Use processErrorCategories with metadata parameter.');
  
  if (!data || !Array.isArray(data) || data.length === 0) return [];
  
  const firstItem = data[0];
  const errorCategories = [];
  
  // Старая логика - определение по исключению стандартных полей
  const standardFields = [
    'date', 'page_loads', 'sms_success', 'registration_success', 
    'registration_unfinished_sms', 'registration_unfinished_form',
    'total_errors', 'sms_errors_percentage', 'registration_errors_percentage', 
    'success_rate', 'ratio'
  ];
  
  Object.keys(firstItem).forEach(key => {
    if (typeof key === 'string' && 
        !standardFields.includes(key) && 
        !key.endsWith('_percentage')) {
      errorCategories.push(key);
    }
  });
  
  return errorCategories;
};

// ========================================
// ASYNC HELPERS (НОВЫЕ)
// ========================================

/**
 * Инициализирует метаданные колонок для компонента
 * @returns {Promise<Array>} Метаданные колонок
 */
export const initializeColumnsMetadata = async () => {
  try {
    const metadata = await fetchColumnsMetadata();
    return metadata;
  } catch (error) {
    console.error('Failed to initialize columns metadata:', error);
    return [];
  }
};

/**
 * Получает конфигурацию колонок по умолчанию
 * @returns {Promise<Object>} Объект с visibleColumns и columnOrder
 */
export const getDefaultColumnsConfiguration = async () => {
  try {
    const metadata = await fetchColumnsMetadata();
    
    const visibleColumns = metadata
      .filter(col => col.is_visible === true)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(col => col.key);
    
    const columnOrder = metadata
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(col => col.key);
    
    return { visibleColumns, columnOrder };
  } catch (error) {
    console.error('Failed to get default columns configuration:', error);
    return { visibleColumns: [], columnOrder: [] };
  }
};